import { createFileRoute, Link } from '@tanstack/react-router';
import { canAdministerPlatform, canManageProperties, canManageUsers } from '@promanage/core';
import { Chip, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { Icon, type IconName } from '../components/Icon';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useCurrentOrg, useSession } from '../features/auth/session';
import { DefaultAgency } from '../features/auth/DefaultAgency';
import { MyEmailSignature } from '../features/auth/MyEmailSignature';
import { ProfilePhoto } from '../features/auth/ProfilePhoto';
import { roleLabel } from '../features/users/format';
import { ThemeOptions } from '../components/ThemeToggle';

export const Route = createFileRoute('/settings/')({ component: SettingsIndex });

function SettingsIndex() {
  const session = useSession();
  const org = useCurrentOrg();
  const roles = session?.roles ?? [];
  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="Settings" subtitle={org?.name ?? 'Your account and agency preferences'} />
      <SettingsNav />
      <div className="grid items-start gap-4 xl:grid-cols-3">
        <div className="space-y-4">
          <Panel>
            <PanelHeader title="Your account" subtitle="Personal details and sign-in security" />
            <div className="space-y-5 px-5 pb-5">
              <div>
                <h2 className="text-[0.9375rem] font-bold">
                  {session?.user.name ?? 'Your profile'}
                </h2>
                <p className="mt-1 break-all text-[0.8125rem] text-ink-muted">
                  {session?.user.email}
                </p>
              </div>
              {session ? (
                <ProfilePhoto name={session.user.name} />
              ) : (
                <div className="h-24 animate-pulse rounded-xl bg-surface-sunk" />
              )}
              <div className="border-t border-rule pt-4">
                <p className="mb-2 text-[0.75rem] font-semibold text-ink-muted">
                  Roles in this agency
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((role) => (
                    <Chip key={role}>{roleLabel(role)}</Chip>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-4">
                <div className="flex items-center gap-2 text-[0.8125rem] font-semibold">
                  <Icon name="shield" size={18} className="text-ink-muted" />
                  Authenticator
                </div>
                {session?.mfaEnrolled ? (
                  <Chip tone="positive">Set up</Chip>
                ) : (
                  <Chip tone="neutral">Not set up</Chip>
                )}
              </div>
              <DefaultAgency />
              <MyEmailSignature />
            </div>
          </Panel>
          <Panel>
            <PanelHeader
              title="Appearance"
              subtitle="Choose how the console looks on this device."
            />
            <div className="px-5 pb-5">
              <ThemeOptions segmented />
            </div>
          </Panel>
        </div>
        <div className="space-y-4 xl:col-span-2">
          <Panel>
            <PanelHeader title="Agency" subtitle="People, tax and payment controls" />
            <div className="px-3 pb-3">
              {canManageUsers(roles) ? (
                <SettingLink
                  to="/settings/users"
                  icon="people"
                  title="Users & access"
                  description="Invite colleagues, manage roles and suspend access."
                />
              ) : null}
              <SettingLink
                to="/settings/tax"
                icon="report"
                title="Tax"
                description="GST registration and management fee tax treatment."
              />
              <SettingLink
                to="/settings/payments"
                icon="bank"
                title="Payments"
                description="Review payment safeguards and approval requirements."
              />
              <SettingLink
                to="/settings/integrations"
                icon="swap"
                title="Integrations"
                description="Connect the agency’s accounts on other services, such as Trade Me."
              />
              {canAdministerPlatform(roles) ? (
                <SettingLink
                  to="/settings/organisations"
                  icon="property"
                  title="Organisations"
                  description="Manage agencies and time-limited support access."
                />
              ) : null}
              {canAdministerPlatform(roles) ? (
                <SettingLink
                  to="/settings/unit-costs"
                  icon="ledger"
                  title="Unit costs"
                  description="What running ProManage costs per property, and each agency's match rate."
                />
              ) : null}
            </div>
          </Panel>
          <Panel>
            <PanelHeader
              title="Documents & workflows"
              subtitle="Configure the work your agency repeats"
            />
            <div className="grid px-3 pb-3 md:grid-cols-2">
              <SettingLink
                to="/settings/delivery"
                icon="calendar"
                title="Delivery"
                description="Owner statement delivery and scheduling."
              />
              <SettingLink
                to="/settings/branding"
                icon="image"
                title="Branding"
                description="Your logo, email typeface, colour and footer."
              />
              <SettingLink
                to="/settings/statements"
                icon="bank"
                title="Statement formats"
                description="Bank CSV formats and column mappings."
              />
              <SettingLink
                to="/settings/templates"
                icon="report"
                title="Templates"
                description="Reusable correspondence templates for your agency."
              />
              <SettingLink
                to="/settings/inspections"
                icon="check"
                title="Inspections"
                description="Inspection templates and visit schedules."
              />
              {canManageProperties(roles) ? (
                <SettingLink
                  to="/settings/import"
                  icon="arrow-right"
                  title="Import from CSV"
                  description="Bring across owners, properties, tenancies and people."
                />
              ) : null}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function SettingLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: IconName;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-surface-sunk"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-sunk text-ink-muted ring-1 ring-rule">
        <Icon name={icon} size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.8125rem] font-semibold group-hover:text-accent">
          {title}
        </span>
        <span className="mt-1 block text-[0.75rem] leading-relaxed text-ink-muted">
          {description}
        </span>
      </span>
      <Icon
        name="chevron-right"
        size={16}
        className="shrink-0 text-ink-subtle group-hover:text-accent"
      />
    </Link>
  );
}
